import { useCallback, useEffect, useRef, useState } from 'react'
import { useStore } from 'react-redux'
import { baseQueryWithReauth } from '@/api/setup/baseQuery'
import useRevokeObjectURL from './useRevokeObjectURL'

const BASE_URL = import.meta.env.DEV
  ? '/processor-proxy'
  : import.meta.env.VITE_PROCESSOR_API

/**
 * @typedef {{ blob: Blob, contentType: string, objectUrl: string }} BlobResult
 */

/**
 * @param {Response} response
 * @returns {Promise<BlobResult | unknown>}
 */
async function handleBlobResponse(response) {
  const contentType = response.headers.get('content-type') || ''

  if (!response.ok) {
    if (contentType.includes('application/json')) {
      return response.json()
    }
    return response.text()
  }

  if (contentType.includes('application/json')) {
    const json = await response.json()
    throw new Error(json?.message || JSON.stringify(json))
  }

  const blob = await response.blob()
  const type = contentType.split(';')[0] || blob.type
  const typedBlob = type ? new Blob([blob], { type }) : blob

  return {
    blob: typedBlob,
    contentType: type,
    objectUrl: window.URL.createObjectURL(typedBlob)
  }
}

/**
 * Fetch a blob (image or file) through baseQueryWithReauth.
 * Pass a relative API path; leave `url` empty or set `skip` to wait for a later refetch.
 * `refetch(overrides)` can supply a one-off url, params, headers, or method.
 *
 * @param {string | null | undefined} url
 * @param {{
 *   skip?: boolean,
 *   params?: Record<string, unknown>,
 *   headers?: Record<string, string>,
 *   method?: string
 * }} [options]
 */
const useFetchBlob = (url, options = {}) => {
  const { skip = !url, params, headers, method = 'GET' } = options
  const store = useStore()
  const [blob, setBlob] = useState(/** @type {Blob | null} */ (null))
  const [objectUrl, setObjectUrl] = useState('')
  const [contentType, setContentType] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(/** @type {any} */ (null))
  const abortRef = useRef(/** @type {AbortController | null} */ (null))
  const requestIdRef = useRef(0)

  useRevokeObjectURL(objectUrl)

  const paramsKey = params ? JSON.stringify(params) : ''
  const headersKey = headers ? JSON.stringify(headers) : ''

  const invalidateInFlight = () => {
    abortRef.current?.abort()
    abortRef.current = null
    requestIdRef.current += 1
  }

  const refetch = useCallback(async (
    overrides = /** @type {{
      url?: string,
      params?: Record<string, unknown>,
      headers?: Record<string, string>,
      method?: string
    }} */ ({})
  ) => {
    const requestUrl = overrides.url ?? url
    if (!requestUrl) {
      return { blob: null, objectUrl: '', error: null }
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const requestId = ++requestIdRef.current

    setIsLoading(true)
    setError(null)

    const requestParams =
      overrides.params !== undefined
        ? overrides.params
        : paramsKey
          ? JSON.parse(paramsKey)
          : undefined
    const requestHeaders =
      overrides.headers !== undefined
        ? overrides.headers
        : headersKey
          ? JSON.parse(headersKey)
          : undefined

    const result = await baseQueryWithReauth({ baseUrl: BASE_URL })(
      {
        url: requestUrl,
        method: overrides.method ?? method,
        params: requestParams,
        headers: requestHeaders,
        responseHandler: handleBlobResponse,
        cache: 'no-cache'
      },
      {
        getState: store.getState,
        dispatch: store.dispatch,
        extra: undefined,
        endpoint: 'fetchBlob',
        type: 'query',
        signal: controller.signal,
        abort: () => controller.abort()
      },
      {}
    )

    const resultUrl = /** @type {BlobResult | undefined} */ (result.data)?.objectUrl
    const isCurrent =
      requestId === requestIdRef.current && abortRef.current === controller

    if (!isCurrent || controller.signal.aborted) {
      if (resultUrl) window.URL.revokeObjectURL(resultUrl)
      return { blob: null, objectUrl: '', error: null }
    }

    if (result.error) {
      setError(result.error)
      setBlob(null)
      setObjectUrl('')
      setContentType('')
      setIsLoading(false)
      return { blob: null, objectUrl: '', error: result.error }
    }

    const data = /** @type {BlobResult} */ (result.data)
    setBlob(data.blob)
    setObjectUrl(data.objectUrl)
    setContentType(data.contentType)
    setIsLoading(false)
    return { blob: data.blob, objectUrl: data.objectUrl, error: null }
  }, [url, method, paramsKey, headersKey, store])

  useEffect(() => {
    if (skip || !url) {
      invalidateInFlight()
      setIsLoading(false)
      setError(null)
      setBlob(null)
      setObjectUrl('')
      setContentType('')
      return undefined
    }

    refetch()

    return () => {
      invalidateInFlight()
    }
  }, [url, skip, refetch])

  return { blob, objectUrl, contentType, isLoading, error, refetch }
}

export default useFetchBlob
